import { CSSProperties, useEffect, useRef, useState } from "react";
import { CreativeSelectorPost } from "Hooks/useCampaignBuilder";
import useScreen from "Hooks/useScreen";
import Colors from "modules/Colors";
import { Caption } from "Hooks/useCampaignBuilder";
import { AssetPlatformType } from "components/CampaignViews/AdCreativeSelectorCampaignView";
import Icon from "elements/Icon";
import { MediaType } from "modules/Types";
import Sty from "modules/Sty";
import { clsx, styx } from "modules/Utils";
import DebugBox from "components/shareable/DebugBox";

interface AdPreviewProps {
    className?: string,
    style?: CSSProperties,
    username: string | null,
    callToActionLabel: string,
    profilePicUrl: string | null,
    previewedAsset: CreativeSelectorPost | null,
    captions: Caption[],
    previewType: string | null | 'ig-story' | 'ig-feed' | 'ig-reels' | 'tiktok',
}

// NOTE: component()
const AdPreview = ({
    className,
    style,
    username,
    callToActionLabel,
    profilePicUrl,
    previewedAsset,
    captions,
    previewType
}: AdPreviewProps) => {
    const { mStyle, mobileView } = useScreen();
    /** Current index of the carousel being displayed */
    const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.load();
        }
    }, [previewedAsset]);

    /** Navigate to previous image in carousel */
    const goToPrevious = () => {
        if (!previewedAsset?.children || previewedAsset.children.length === 0) return;
        setCurrentCarouselIndex(current =>
            current === 0 ? previewedAsset.children!.length - 1 : current - 1
        );
    };

    /** Navigate to next image in carousel */
    const goToNext = () => {
        if (!previewedAsset?.children || previewedAsset.children.length === 0) return;
        setCurrentCarouselIndex(current =>
            current === previewedAsset.children!.length - 1 ? 0 : current + 1
        );
    };

    /** Reset carousel index when post changes */
    if (previewedAsset?.id && currentCarouselIndex > 0 &&
        (!previewedAsset.children || currentCarouselIndex >= previewedAsset.children.length)) {
        setCurrentCarouselIndex(0);
    }

    const renderIGAdArtistName = ({
        textColor = Colors.white,
        showTextShadow = false
    }: {
        textColor?: string,
        showTextShadow?: boolean
    }) => {
        if (!username || !previewedAsset) {
            return null
        }

        return (
            <div className="artistInfoContainer" style={sty.artistInfoContainer}>
                {profilePicUrl &&
                    <img
                        className="artistAvatar"
                        style={sty.artistAvatar}
                        src={profilePicUrl}
                        alt="Profile Picture"
                    />
                }
                <span
                    className="artistName"
                    style={{
                        ...sty.artistName,
                        color: textColor,
                        textShadow: showTextShadow ? '0px 0px 10px rgba(0, 0, 0, 0.5)' : 'none'
                    }}
                >
                    {username}
                </span>
            </div>
        )
    }

    const renderIGStoryBottomArea = () => {
        return (
            <div
                className="storyPreviewActionArea"
                style={sty.storyPreviewActionArea}
            >
                <div className="storyPreviewActionContent" style={sty.storyPreviewActionContent}>
                    <Icon
                        className="storyPreviewActionIcon"
                        icon="chevronDown"
                        style={sty.swipeUpArrow}
                        size={19}
                        color="white"
                    />
                    <div
                        className="storyPreviewCallToAction"
                        style={mStyle(sty, "callToAction")}
                    >
                        {callToActionLabel}
                    </div>
                </div>
            </div>
        )
    }

    const renderStoryPreview = () => {
        return (
            <>
                <div className="artistNameContainer" style={sty.artistNameContainer}>
                    <div
                        className="storyPreviewTopTimerBar"
                        style={{
                            width: '100%',
                            height: 4,
                            display: 'flex',
                            gap: 4,
                            justifyContent: 'space-between'
                        }}>
                        <div style={{ flex: 1, height: '100%', backgroundColor: 'white' }} />
                        <div style={{ flex: 1, height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
                        <div style={{ flex: 1, height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
                        <div style={{ flex: 1, height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
                        <div style={{ flex: 1, height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        padding: '8px 16px',
                        width: '100%'
                    }}>
                        {renderIGAdArtistName({
                            textColor: Colors.white,
                            showTextShadow: true
                        })}
                    </div>
                </div>

                {renderPreviewedAsset({
                    cover: true,
                    type: 'story'
                })}
                {renderIGStoryBottomArea()}
            </>
        )
    }

    const renderFeedPreview = () => {
        // Get the appropriate caption based on post type
        let caption = '';

        if (previewedAsset) {
            switch (previewedAsset.originalPostType) {
                case AssetPlatformType.INSTAGRAM:
                    caption = previewedAsset.platformCaption || '';
                    break;
                case AssetPlatformType.TIKTOK:
                    caption = previewedAsset.platformCaption || '';
                    break;
                case AssetPlatformType.CLOUDINARY:
                case AssetPlatformType.IMPORTED_FROM_URL:
                    caption = captions.length > 0 ? captions[0].text : '';
                    break;
                default:
                    caption = '';
            }
        }

        return (
            <div
                className="feedPreviewContainer"
                style={{
                    backgroundColor: Colors.white,
                    borderRadius: 16,
                    overflow: 'hidden',
                    paddingBottom: 8
                }}
            >
                <div className="feedPreviewArtistNameContainer"
                    style={{
                        padding: '8px'
                    }}
                >
                    {renderIGAdArtistName({
                        textColor: Colors.black,
                        showTextShadow: false
                    })}
                </div>
                <div className='border-t'>
                    {renderPreviewedAsset({
                        cover: true,
                        type: 'feed',
                    })}
                </div>
                {callToActionLabel &&
                    <div
                        className='text-white text-base md:text-xs font-semibold p-2 '
                        style={{
                            background: "#00AAFF",
                        }}
                    >
                        {callToActionLabel}
                    </div>
                }
                <div className="feedReviewBottomSection" style={{
                    padding: 8,
                    gap: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between'
                }}>
                    <div className="feedPreviewIcons flex items-center justify-between w-4/12">
                        <Icon
                            className="feedPreviewIcon_heart"
                            icon="heart"
                            color="black"
                        />
                        <Icon
                            className="feedPreviewIcon_comments"
                            icon="comments"
                            color="black"
                        />
                        <Icon
                            className="feedPreviewIcon_share"
                            icon="send"
                            color="black"
                        />
                    </div>

                    <div className="feedPreviewCaption break-words">
                        {username &&
                            <span
                                className="feedPreviewUsername"
                                style={{
                                    fontWeight: 600,
                                    marginRight: 4
                                }}
                            >
                                {username}
                            </span>
                        }
                        {caption && <span className="feedPreviewText">{caption}</span>}
                    </div>
                </div>
            </div>
        )
    }

    const renderReelsPreview = () => {
        // Get the appropriate caption based on post type
        let caption = '';

        if (previewedAsset) {
            switch (previewedAsset.originalPostType) {
                case AssetPlatformType.INSTAGRAM:
                    caption = previewedAsset.platformCaption || '';
                    break;
                case AssetPlatformType.TIKTOK:
                    caption = previewedAsset.platformCaption || '';
                    break;
                case AssetPlatformType.CLOUDINARY:
                case AssetPlatformType.IMPORTED_FROM_URL:
                    caption = captions.length > 0 ? captions[0].text : '';
                    break;
                default:
                    caption = '';
            }
        }


        return (
            <>
                <div className="reelsPreviewContainer" style={sty.reelsPreviewContainer}>
                    {renderPreviewedAsset({
                        cover: true,
                        type: 'story'
                    })}
                </div>
                <div
                    className="instagramReelsComponent rounded-xl px-3 absolute z-10 bottom-0 flex flex-col w-full items-center justify-center"
                    style={{
                        background: 'linear-gradient(0deg, rgb(0 0 0 / 40%) 0%, rgb(255 255 255 / 0%) 100%)'
                    }}
                >
                    <div
                        className="my-1 flex justify-end items-center"
                        style={{ width: '100%' }}
                    >
                        <Icon
                            className="heartIcon"
                            icon="heart"
                            size={mobileView ? 24 : 16}
                            color="white"
                        />
                    </div>

                    <div className="my-1 flex justify-end items-center w-full">
                        <Icon
                            className="commentIcon"
                            icon="comments"
                            size={mobileView ? 24 : 16}
                            color="white"
                        />
                    </div>

                    <div className="my-1 flex justify-between items-center w-full">
                        {renderIGAdArtistName({
                            textColor: Colors.white,
                            showTextShadow: true
                        })}
                        <div className="ml-auto">
                            <Icon
                                className="shareIcon"
                                icon="share"
                                size={mobileView ? 24 : 16}
                                color="white"
                            />
                        </div>
                    </div>

                    {caption && (<div className="mb-1 flex items-center w-full text-white">
                        <div
                            className="captionText"
                            style={{
                                fontSize: 14,
                                lineHeight: '18px',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxHeight: 54
                            }}
                        >
                            {caption}
                        </div>
                    </div>)}

                    <div className="w-full text-xs rounded-md text-center w-auto bg-white 
                            bg-opacity-30	
                            text-white
                            font-semibold mb-3
                            px-5 py-3
                            md:px-4 md:py-2 
                            text-sm md:text-xs"
                        style={{
                            borderRadius: 4
                        }}
                    >
                        <p>{callToActionLabel}</p>
                    </div>
                </div>
            </>
        )
    }

    const renderTikTokPreview = () => {
        if (!previewedAsset) {
            return renderNoPreviewSelected();
        }

        const isVideo = previewedAsset.mediaType === MediaType.VIDEO;
        const assetUrl = previewedAsset.assetUrl || '';
        const caption = captions.length > 0 ? captions[0].text : '';
        // Get username from previewedAsset.title for TikTok posts if available
        const displayUsername = username
        return (
            <div className="tikTokPreview" style={sty.tikTokContainer}>
                <div style={sty.contentContainer}>
                    {isVideo ? (
                        <div className="videoContainer" style={sty.videoContainer}>
                            <video
                                style={sty.video}
                                src={assetUrl}
                                poster={previewedAsset.thumbnailUrl}
                                playsInline
                                muted
                                loop
                                controls={false}
                            />
                            <div className="playButton" style={sty.playButton}>
                                <Icon className="playIcon" icon="play" size={24} color="white" />
                            </div>
                        </div>
                    ) : (
                        <img
                            className="image"
                            style={sty.image}
                            src={assetUrl}
                            alt="TikTok preview"
                            onError={(e) => {
                                e.currentTarget.src = '';
                                e.currentTarget.onerror = null;
                            }}
                        />
                    )}

                    <div className="adPreviewControlsOverlay" style={sty.controlsOverlay}>
                        <div className="adPreviewActionButton" style={sty.actionButton}>
                            <Icon className="heart" icon="heart" size={24} color="white" />
                            <span style={sty.actionCount}>
                                {previewedAsset.likesCount ? previewedAsset.likesCount.toLocaleString() : '12.5K'}
                            </span>
                        </div>
                        <div className="adPreviewActionButton" style={sty.actionButton}>
                            <Icon className="adPreviewCommentIcon" icon="comments" size={24} color="white" />
                            <span style={sty.actionCount}>
                                {previewedAsset.commentsCount ? previewedAsset.commentsCount.toLocaleString() : '1.2K'}
                            </span>
                        </div>
                        <div className="adPreviewActionButton" style={sty.actionButton}>
                            <Icon className="share" icon="share" size={24} color="white" />
                            <span style={sty.actionCount}>
                                {previewedAsset.sharesCount ? previewedAsset.sharesCount.toLocaleString() : '8.7K'}
                            </span>
                        </div>
                    </div>

                    <div className="adPreviewProfileInfo" style={sty.profileInfo}>
                        <div className="adPreviewProfileName" style={sty.profileName}>@{displayUsername || 'username'}</div>
                        <div className="adPreviewCaption" style={sty.caption}>{caption}</div>
                        <div className="adPreviewCta" style={sty.cta}>{callToActionLabel}</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPreviewedAsset = (props: {
        cover: boolean,
        maxHeight?: string,
        type: 'feed' | 'story'
    }) => {
        const {
            cover = false,
            maxHeight = null,
            type
        } = props;

        const selectedPost = previewedAsset;
        if (!selectedPost) return null;

        // Define object-fit based on preview type
        const objectFitStyle = type === 'feed' ? 'cover' : (previewType === 'ig-reels' ? 'cover' : 'contain');

        // Set container styles based on preview type
        const containerStyle = {
            ...sty.adPreviewPostContainer,
            height: type === 'story' ? '100%' : 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative' as 'relative'
        };

        // Check if this is a carousel post with multiple items
        const isCarousel = selectedPost.mediaType === MediaType.CAROUSEL_ALBUM &&
            selectedPost.children &&
            selectedPost.children.length > 1;

        // Determine which URL to display
        let displayUrl = selectedPost.assetUrl;
        let displayType = selectedPost.mediaType === MediaType.VIDEO ? MediaType.VIDEO : MediaType.IMAGE;

        // For carousel posts, use the current child's URL
        if (isCarousel && selectedPost.children) {
            const currentChild = selectedPost.children[currentCarouselIndex];
            if (currentChild) {
                displayUrl = currentChild.mediaUrl || selectedPost.assetUrl;
                displayType = currentChild.mediaType || displayType;
            }
        }

        // Render the asset based on its type
        let assetElement: JSX.Element;
        if (displayType === MediaType.VIDEO) {
            assetElement = (
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls={false}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: objectFitStyle as 'cover' | 'contain'
                    }}
                    ref={videoRef}
                >
                    <source src={displayUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        } else {
            assetElement = (
                <img
                    src={displayUrl}
                    alt="Ad Preview"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: objectFitStyle as 'cover' | 'contain'
                    }}
                    onError={(e) => {
                        const img = e.currentTarget;
                        if (img.src !== '') {
                            console.error('Failed to load image in AdPreview');
                            img.src = '';
                            img.alt = 'Failed to load image';
                            img.style.backgroundColor = '#f0f0f0';
                        }
                    }}
                />
            );
        }

        return (
            <div
                style={containerStyle}
                className={clsx(
                    'adPreviewPostContainer',
                    { 'carouselContainer': isCarousel }
                )}
            >
                {assetElement}

                {isCarousel &&
                    <>
                        <div
                            className="carouselNavigation"
                            style={sty.carouselNavigation}
                        >
                            <button
                                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                                style={sty.carouselButton}
                                className="carouselPrevButton"
                            >
                                <Icon
                                    icon="chevronLeft"
                                    size={24}
                                    color="white"
                                    className="carouselPrevIcon"
                                />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                                style={sty.carouselButton}
                                className="carouselNextButton"
                            >
                                <Icon
                                    icon="chevronRight"
                                    size={24}
                                    color="white"
                                    className="carouselNextIcon"
                                />
                            </button>
                        </div>

                        <div
                            className="carouselIndicators"
                            style={sty.carouselIndicators}
                        >
                            {selectedPost.children?.map((_, index) => (
                                <div
                                    key={`carousel-indicator-${index}`}
                                    className={clsx('carouselIndicator', { 'active': index === currentCarouselIndex })}
                                    style={styx(
                                        sty.carouselIndicator,
                                        index === currentCarouselIndex && sty.carouselIndicatorActive
                                    )}
                                    onClick={(e) => { e.stopPropagation(); setCurrentCarouselIndex(index); }}
                                />
                            ))}
                        </div>
                    </>
                }
            </div>
        );
    }

    const renderNoPreviewSelected = () => {
        return (
            <div
                className="adPreviewNoImageAvailableContainer"
                style={{
                    backgroundColor: Colors.greyLight,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 10,
                    height: '100%',
                    width: '100%',
                    padding: 16
                }}
            >
                <Icon
                    className="adPreviewNoImageAvailable"
                    icon="noImageAvailable"
                    size={42}
                />
                <div className="uploadAdVideosText" style={Sty.text16}>
                    Upload your ad videos to preview your ad.
                </div>
            </div>
        )
    }

    let renderedPreview: JSX.Element | null = null;

    if (previewType) {
        switch (previewType) {
            case 'ig-story':
                renderedPreview = renderStoryPreview()
                break
            case 'ig-feed':
                renderedPreview = renderFeedPreview()
                break
            case 'ig-reels':
                renderedPreview = renderReelsPreview()
                break
            case 'tiktok':
                renderedPreview = renderTikTokPreview()
                break
        }
    } else {
        renderedPreview = renderNoPreviewSelected()
    }

    // NOTE: render()
    return (
        <div
            className={clsx(
                "adPreviewElement",
                previewType,
                className,
                { "bg-transparent": previewType === 'ig-feed' }
            )}
            style={styx(
                sty.adPreviewElement,
                previewType === 'ig-feed' && sty.feedPreviewElement,
                style
            )}
        >
            <div
                className={clsx("adPreviewContent", {
                    'storyPreviewContent': previewType === 'ig-story',
                    'reelsPreviewContent': previewType === 'ig-reels',
                    'feedPreviewContent': previewType === 'ig-feed'
                })}
                style={styx(
                    sty.adPreviewContent,
                    previewType === 'ig-feed' && sty.feedPreviewContent,
                    (previewType === 'ig-story' || previewType === 'ig-reels') && sty.verticalContent
                )}
            >
                {/* <DebugBox
                    alwaysShow
                    data={{
                        previewedAsset
                    }}
                /> */}
                {renderedPreview}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    adPreviewElement: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        gap: 16,
        boxShadow: Sty.boxShadowInset,
        width: '100%',
        height: '100%'
    },
    feedPreviewElement: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
        padding: 0
    },
    adPreviewPostContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        background: 'black',
        overflow: 'hidden'
    },
    reelsPreviewContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden'
    },
    artistInfoContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8
    },
    artistAvatar: {
        height: 24,
        width: 24,
        borderRadius: '50%',
        objectFit: 'cover'
    },
    artistName: {
        color: 'white',
        fontSize: 14,
        fontWeight: 500,
    },
    adPreviewContent: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 8,
        position: 'relative',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        alignSelf: 'flex-start',
    },
    feedPreviewContent: {
        boxShadow: 'none',
        border: '1px solid rgba(229, 231, 235)',
        borderRadius: 16,
    },
    verticalContent: {
        aspectRatio: '9/16',
        width: '100%',
        height: '100%',
        backgroundColor: 'black'
    },
    storyPreviewActionArea: {
        position: 'absolute',
        bottom: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        marginBottom: 8
    },
    storyPreviewActionContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
    },
    artistNameContainer: {
        display: 'flex',
        position: 'absolute',
        top: 1,
        left: 0,
        alignItems: 'center',
        gap: 8,
        flexDirection: 'column',
        width: '100%',
        borderRadius: 20
    },
    swipeUpArrow: {
        rotate: '180deg'
    },
    callToAction: {
        borderRadius: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        color: 'white',
        fontWeight: 600,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 20,
        paddingRight: 20,
        fontSize: 14,
        textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)'
    },
    callToAction_mobile: {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 20,
        paddingRight: 20,
        fontSize: 12,
    },
    carouselNavigation: {
        position: 'absolute',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        pointerEvents: 'none',
        height: '100%',
        alignItems: 'center',
        padding: '0 10px'
    },
    carouselButton: {
        background: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '50%',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        cursor: 'pointer',
        pointerEvents: 'all'
    },
    carouselIndicators: {
        position: 'absolute',
        bottom: 10,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        gap: 4
    },
    carouselIndicator: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.5)',
        cursor: 'pointer'
    },
    carouselIndicatorActive: {
        background: 'white'
    },
    tikTokContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 8,
        backgroundColor: 'black',
        display: 'flex',
        flexDirection: 'column'
    },
    contentContainer: {
        position: 'relative',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    videoContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
    },
    video: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    controlsOverlay: {
        position: 'absolute',
        right: 12,
        bottom: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12
    },
    actionButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
    },
    actionCount: {
        color: 'white',
        fontSize: 12
    },
    profileInfo: {
        position: 'absolute',
        left: 12,
        bottom: 12,
        display: 'flex',
        flexDirection: 'column',
        color: 'white'
    },
    profileName: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 4
    },
    caption: {
        fontSize: 14,
        maxWidth: 250,
        marginBottom: 8
    },
    cta: {
        backgroundColor: 'rgba(254, 44, 85, 1)',
        color: 'white',
        padding: '6px 12px',
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 600,
        alignSelf: 'flex-start'
    },
    playButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 60,
        height: 60,
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
    }
}

export default AdPreview;